import lodashNoop from "lodash/fp/noop";
import { useCallback, useEffect, useState, useRef } from "react";
import { useAppState } from "hub/src/hooks/useAppState";

export enum WebSocketStatus {
  Disconnected = "Disconnected",
  Connecting = "Connecting",
  Connected = "Connected",
  Reconnecting = "Reconnecting",
  Error = "Error",
  Failed = "Failed",
}

export interface WebSocketConfig<Type> {
  url: string;
  onMessage?: (data: Type) => void;
  onStatusChange?: (status: WebSocketStatus) => void;
  reconnectionInterval?: number;
  reconnectionAttempts?: number;
  logging?: boolean;
}

export class WebSocketManager<InMessage, OutMessage> {
  private config: Required<WebSocketConfig<InMessage>>;

  private socket: WebSocket | null = null;
  private status: WebSocketStatus = WebSocketStatus.Disconnected;
  private reconnectionAttempt = 0;
  private reconnectionTimeout: NodeJS.Timeout | null = null;
  private isIntentionallyClosed = false;

  constructor(config: WebSocketConfig<InMessage>) {
    this.config = {
      url: config.url,
      onMessage: config.onMessage || lodashNoop,
      onStatusChange: config.onStatusChange || lodashNoop,
      reconnectionInterval: config.reconnectionInterval || 3000,
      reconnectionAttempts: config.reconnectionAttempts || 10,
      logging: config.logging || false,
    };
  }

  public initialize(): void {
    this.connect();
    this.addNetworkEventListeners();
  }

  public terminate(): void {
    this.disconnect();
    this.removeNetworkEventListeners();
  }

  public send(message: OutMessage): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      this.log("Cannot send message - not connected");
      return false;
    }

    const data = JSON.stringify(message);
    this.socket.send(data);
    this.log("Message sent:", message);
    return true;
  }

  private connect(): void {
    try {
      this.isIntentionallyClosed = false;
      this.setStatus(WebSocketStatus.Connecting);
      this.socket = new WebSocket(this.config.url);
      this.addSocketEventListeners();
    } catch (error) {
      this.log("Connection error:", error);
      this.setStatus(WebSocketStatus.Error);
      this.scheduleReconnection();
    }
  }

  private disconnect(): void {
    this.isIntentionallyClosed = true;
    this.clearReconnectionTimeout();

    if (this.socket) {
      this.removeSocketEventListeners();
      this.socket.close();
      this.socket = null;
    }

    this.setStatus(WebSocketStatus.Disconnected);
  }

  private addSocketEventListeners(): void {
    if (!this.socket) {
      return;
    }

    this.socket.addEventListener("open", this.handleOpen);
    this.socket.addEventListener("message", this.handleMessage);
    this.socket.addEventListener("close", this.handleClose);
    this.socket.addEventListener("error", this.handleError);
  }

  private removeSocketEventListeners(): void {
    if (!this.socket) {
      return;
    }

    this.socket.removeEventListener("open", this.handleOpen);
    this.socket.removeEventListener("message", this.handleMessage);
    this.socket.removeEventListener("close", this.handleClose);
    this.socket.removeEventListener("error", this.handleError);
  }

  private addNetworkEventListeners(): void {
    window.addEventListener("online", this.handleOnline);
  }

  private removeNetworkEventListeners(): void {
    window.removeEventListener("online", this.handleOnline);
  }

  private handleOpen = (): void => {
    this.log("Connected");
    this.setStatus(WebSocketStatus.Connected);
    this.reconnectionAttempt = 0;
    this.clearReconnectionTimeout();
  };

  private handleMessage = (event: MessageEvent): void => {
    try {
      const data: InMessage = JSON.parse(event.data);
      this.log("Message received:", data);
      this.config.onMessage(data);
    } catch (error) {
      this.log("Error parsing message:", error);
    }
  };

  private handleClose = (event: CloseEvent): void => {
    this.log("Disconnected:", event.code, event.reason);
    this.setStatus(WebSocketStatus.Disconnected);

    if (!this.isIntentionallyClosed) {
      this.scheduleReconnection();
    }
  };

  private handleError = (error: Event): void => {
    this.log("Error:", error);
    this.setStatus(WebSocketStatus.Error);
  };

  private handleOnline = (): void => {
    this.log("Internet connection restored");

    const statuses = [
      WebSocketStatus.Failed,
      WebSocketStatus.Disconnected,
      WebSocketStatus.Error,
      WebSocketStatus.Reconnecting,
    ];

    const shouldReconnect =
      !this.isIntentionallyClosed && statuses.includes(this.status);

    if (shouldReconnect) {
      this.log("Attempting to reconnect after internet restoration");
      this.clearReconnectionTimeout();
      this.reconnectionAttempt = 0;
      this.connect();
    }
  };

  private scheduleReconnection(): void {
    if (this.reconnectionAttempt >= this.config.reconnectionAttempts) {
      this.log("Max reconnection attempts reached");
      this.setStatus(WebSocketStatus.Failed);
      return;
    }

    this.clearReconnectionTimeout();
    this.reconnectionAttempt++;

    const delay = this.config.reconnectionInterval;
    this.log(
      `Reconnecting in ${delay}ms (attempt ${this.reconnectionAttempt})`,
    );
    this.setStatus(WebSocketStatus.Reconnecting);

    this.reconnectionTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private clearReconnectionTimeout(): void {
    if (this.reconnectionTimeout) {
      clearTimeout(this.reconnectionTimeout);
      this.reconnectionTimeout = null;
    }
  }

  private setStatus(status: WebSocketStatus): void {
    this.status = status;
    this.config.onStatusChange(status);
  }

  private log(...messages: unknown[]): void {
    if (this.config.logging) {
      console.log("[WebSocket]", ...messages);
    }
  }
}

interface UseWebSocketOptions {
  url: string;
  disabled?: boolean;
  reconnectionInterval?: number;
  reconnectionAttempts?: number;
  logging?: boolean;
}

interface UseWebSocketReturn<InMessage, OutMessage> {
  status: WebSocketStatus;
  lastMessage: InMessage | null;
  send: (message: OutMessage) => boolean;
}

export const useWebSocket = <InMessage, OutMessage>(
  options: UseWebSocketOptions,
): UseWebSocketReturn<InMessage, OutMessage> => {
  const [status, setStatus] = useState<WebSocketStatus>(
    WebSocketStatus.Disconnected,
  );
  const [lastMessage, setLastMessage] = useState<InMessage | null>(null);

  const appState = useAppState();

  const disabled = options?.disabled || !appState?.isActive;

  const webSocketManagerRef = useRef<WebSocketManager<
    InMessage,
    OutMessage
  > | null>(null);

  const send = useCallback(
    (message: OutMessage): boolean => {
      return webSocketManagerRef.current?.send(message) || false;
    },
    [webSocketManagerRef],
  );

  useEffect(() => {
    if (disabled) {
      return;
    }

    webSocketManagerRef.current = new WebSocketManager<InMessage, OutMessage>({
      url: options.url,
      onMessage: (data) => setLastMessage(data),
      onStatusChange: (newStatus: WebSocketStatus) => setStatus(newStatus),
      reconnectionInterval: options?.reconnectionInterval,
      reconnectionAttempts: options?.reconnectionAttempts,
      logging: options?.logging,
    });

    webSocketManagerRef.current.initialize();

    return () => {
      webSocketManagerRef.current?.terminate();
      webSocketManagerRef.current = null;
    };
  }, [disabled, options.url]);

  return {
    status,
    lastMessage,
    send,
  };
};
